import mantineCoreCss from "@mantine/core/styles.css?url";
import mantineDateCss from "@mantine/dates/styles.css?url";
import mantineDropzoneCss from "@mantine/dropzone/styles.css?url";
import { Notifications } from "@mantine/notifications";
import mantineNotificationsCss from "@mantine/notifications/styles.css?url";
import { nprogress } from "@mantine/nprogress";
import mantineNavProgressCss from "@mantine/nprogress/styles.css?url";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRootRouteWithContext, HeadContent, Outlet, useRouterState } from "@tanstack/react-router";
import { AllEnterpriseModule, LicenseManager, ValidationModule } from "ag-grid-enterprise";
import { AgGridProvider } from "ag-grid-react";
import mantineTableCss from "mantine-react-table/styles.css?url";
import mobiscrollCss from "mobiscroll/dist/css/mobiscroll.min.css?url";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { useEffect } from "react";
import { loadDexieDataToMemoryCache } from "~/dexie/sync";
import { env } from "~/env";
import { DesktopLayout } from "~/layouts/desktop-layout";
import { authClient } from "~/lib/auth-client";
import { logger } from "~/lib/logger";
import type { trpc } from "~/lib/trpc";
import appCss from "~/styles/index.css?url";

const agGridModules = [
  AllEnterpriseModule,
  ...(!import.meta.env.PROD ? [ValidationModule] : []),
];
LicenseManager.setLicenseKey(env.VITE_AG_GRID_KEY);

export interface RouterAppContext {
  trpc: typeof trpc;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Perforia CRM" },
      { name: "apple-mobile-web-app-title", content: "Perforia CRM" },
    ],
    links: [
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap",
      },
      { rel: "stylesheet", href: mantineCoreCss },
      { rel: "stylesheet", href: mantineDateCss },
      { rel: "stylesheet", href: mantineDropzoneCss },
      { rel: "stylesheet", href: mantineTableCss },
      { rel: "stylesheet", href: mantineNotificationsCss },
      { rel: "stylesheet", href: mantineNavProgressCss },
      { rel: "stylesheet", href: mobiscrollCss },
      { rel: "stylesheet", href: appCss },
    ],
  }),
  beforeLoad: async () => {
    logger.debug("before load called");
    const session = await authClient.getSession();
    await loadDexieDataToMemoryCache();
    return { session: session.data };
  },
  component: RootComponent,
});

function RootComponent() {
  const isFetching = useRouterState({ select: (s) => s.isLoading });

  useEffect(() => {
    if (isFetching) nprogress.start();
    else nprogress.complete();
  }, [isFetching]);

  return (
    <>
      <HeadContent />
      <NuqsAdapter>
        <DesktopLayout>
          <AgGridProvider modules={agGridModules}>
            <Outlet />
          </AgGridProvider>
        </DesktopLayout>
        {/* <TanStackRouterDevtools position="bottom-left" /> */}
        <Notifications zIndex={1_000_000_000} />
        <ReactQueryDevtools position="bottom" buttonPosition="bottom-right" />
      </NuqsAdapter>
    </>
  );
}
